/* ── Which row is THE row? ───────────────────────────────────────────────────
 *
 * Stage 3 makes `niche_version_id` part of a row's identity in `outputs`, so a
 * section can now hold more than one row: one per niche she has built it under.
 * That is the point — a rebuild stops overwriting, and her old niche's work
 * survives.
 *
 * It also means the load at App.jsx has to answer a question it never had to
 * answer before. It flattens rows into `moduleOutputs[module][section] = content`
 * and `outputStamps[module][section] = niche_version_id`, and with two rows for
 * one section that flatten is LOSSY: whichever row PostgREST happens to return
 * last silently wins. Which row she sees would depend on row order.
 *
 * So the choice is made HERE, once, at that one boundary. The shape handed back
 * is exactly the shape those two maps already have, which is why the ~70 places
 * downstream that read them need no change at all: `sectionLockState` still
 * compares the stamp it is given against the active niche and still returns
 * LOCKED / PAST / UNSAVED.
 *
 * THE FOUR BRANCHES, in order:
 *
 *   1. the row stamped to the ACTIVE niche — she has built this under the niche
 *      she is on, and nothing else can outrank that.
 *   2. else the NULL-stamped row. Unstamped means pre-versioning, and the whole
 *      app treats that as current. Flipping it to "past" would turn every
 *      section of every coach who never pivoted amber on the day this ships.
 *   3. else the most recent row from ANY niche, which will read as PAST.
 *   4. else absent.
 *
 * BRANCH 3 IS THE ONE THAT WAS MISSING from the first draft of this, and leaving
 * it out is the expensive mistake. The reasoning went "under a new niche, stale
 * work simply stops existing" — but most niche changes are small and almost
 * nothing gets rebuilt, so a section locked under niche 1 and never redone would
 * VANISH: a section she completed, showing empty, with the work still in the
 * database. Today she sees "Past locked", which is both true and more useful.
 *
 * EMPTY CONTENT IS NOT A ROW. A row holding "" is a failed write, not a lock —
 * `sectionLockState` has always said so. Skipping those here means an empty row
 * written under the active niche cannot hide real content from an earlier one,
 * and a section whose only row is empty still reads UNSAVED exactly as it does
 * today.
 *
 * DETERMINISTIC BY CONSTRUCTION. Branch 3 sorts rather than trusting the order
 * rows arrived in — newest `updated_at` first, ties broken on the stamp string.
 * A resolver that depends on PostgREST's ordering is the same bug in a new place.
 */

/** A row holding nothing was a failed write. It is not a lock and not a choice. */
function hasContent(row) {
  return row && row.content != null && String(row.content).trim() !== "";
}

/* Newest first. `updated_at` is a timestamp string, so lexical compare is
   chronological; a missing one sorts last rather than throwing. The stamp is
   only a tiebreak, and only exists so two rows written in the same millisecond
   cannot swap places between loads. */
function newestFirst(a, b) {
  const at = a.updated_at || "";
  const bt = b.updated_at || "";
  if (at !== bt) return at < bt ? 1 : -1;
  const an = a.niche_version_id || "";
  const bn = b.niche_version_id || "";
  return an < bn ? 1 : an > bn ? -1 : 0;
}

/**
 * Flatten `outputs` rows into the two maps App.jsx has always held, choosing one
 * row per section.
 *
 * @param rows          what the `outputs` select returned
 * @param activeNicheId the niche she is on, or null/undefined if unknown
 * @returns {{ outputs: object, stamps: object }}
 */
export function resolveOutputRows(rows, activeNicheId) {
  const bySection = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || !row.module_key || !row.section_name) return;
    if (!hasContent(row)) return;
    const key = row.module_key + " " + row.section_name;
    const list = bySection.get(key);
    if (list) list.push(row);
    else bySection.set(key, [row]);
  });

  const outputs = {};
  const stamps = {};
  bySection.forEach((list) => {
    const chosen =
      (activeNicheId && list.find((r) => r.niche_version_id === activeNicheId))
      || list.find((r) => !r.niche_version_id)
      || list.slice().sort(newestFirst)[0];
    if (!chosen) return;
    const mod = chosen.module_key;
    if (!outputs[mod]) { outputs[mod] = {}; stamps[mod] = {}; }
    outputs[mod][chosen.section_name] = chosen.content;
    stamps[mod][chosen.section_name] = chosen.niche_version_id || null;
  });
  return { outputs, stamps };
}

/* ── The other half: which rows does a write have to be careful of? ──────────
 *
 * Two writes in App.jsx used to be safe only because a section had exactly one
 * row, and both stop being safe the moment it can have several.
 *
 * `sectionsAlreadyUnder` answers "does this section already have a row stamped
 * to this niche?" — the question both of them need. The null backfill must not
 * stamp an old row to the active niche when an active-niche row already exists,
 * and "Still true" must not copy a past section forward on top of one. Either
 * would be a unique violation on the new key: today the update simply fails and
 * nothing tells her.
 */
export function sectionsAlreadyUnder(rows, nicheId) {
  const seen = new Set();
  if (!nicheId) return seen;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row && row.niche_version_id === nicheId) {
      seen.add(row.module_key + " " + row.section_name);
    }
  });
  return seen;
}

/** The key `sectionsAlreadyUnder` returns, so callers never build it by hand. */
export function sectionKey(moduleKey, sectionName) {
  return moduleKey + " " + sectionName;
}
